import processResponseResults from './processResponseResults';
import getTransactionIdFromResponse from 'owa-recipient-common/lib/utils/getTransactionIdFromResponse';
import { lazySearchSuggestions } from 'owa-recipient-suggestions';
import { addPendingRequest, removePendingRequest } from '../updatePendingRequestActions';
import { now } from 'owa-datetime';
import { getGuid } from 'owa-guid';
import addToCache from 'owa-recipient-cache/lib/actions/addToCache';
import processSubstrateSuggestionsResponse from 'owa-recipient-common/lib/actions/processSubstrateSuggestionsResponse';
import FindControlViewState from 'owa-recipient-types/lib/types/FindControlViewState';
import RecipientWellWithFindControlViewState from 'owa-recipient-types/lib/types/RecipientWellWithFindControlViewState';
import addTransactionComplete from 'owa-recipient-common/lib/utils/addTransactionComplete';
import addTransactionFailed from 'owa-recipient-common/lib/utils/addTransactionFailed';
import { SubstrateSearchSuggestionsResponse } from 'owa-search-service';
import FindPeopleResponseMessage from 'owa-service/lib/contract/FindPeopleResponseMessage';
import PersonaType from 'owa-service/lib/contract/PersonaType';
import shouldUse3SPeopleSuggestions from 'owa-recipient-suggestions/lib/util/shouldUse3SPeopleSuggestions';
import { action } from 'satcheljs/lib/legacy';
import {
    populateFeedbackStateNetwork,
    updateFeedbackStateNetwork,
} from '../../utils/populateFeedbackState';

const MAX_ENTRIES_TYPEDOWN = 5;

export default action('backfillCache')(async function backfillCache(
    recipientWell: FindControlViewState | RecipientWellWithFindControlViewState,
    queryString: string
): Promise<void> {
    const correlationId = getGuid();
    addPendingRequest(correlationId);
    let transactionStartTime = now();
    // Record the beginning of the live call
    // in case the live call hangs and the user submits feedback before it returns
    populateFeedbackStateNetwork(
        recipientWell,
        transactionStartTime,
        correlationId,
        false /*isDirectory*/
    );
    let transactionId = '';
    try {
        const serverResults = await lazySearchSuggestions
            .import()
            .then(getSuggestions =>
                getSuggestions(queryString, false /*searchDirectory*/, correlationId)
            );
        transactionId = getTransactionIdFromResponse(serverResults);

        const resultSet = getPersonaResults(serverResults);

        // Backfill the cache with server results
        addToCache(resultSet);

        // If there are server results, use the server results
        if (!isServerResultEmpty(serverResults) && recipientWell.queryString == queryString) {
            const hasCacheResults = recipientWell.findResultSet.length > 0;
            updateFeedbackStateNetwork(recipientWell, false /*isDirectory*/, serverResults);

            // Process the response results to update the UI. If the UI already has cache results for this query, this will append the server results to the end of the list.
            // Otherwise it will clear out the existing find result set (from a previous query) and replace with the live results
            processResponseResults(
                recipientWell.findResultSet,
                serverResults,
                hasCacheResults,
                MAX_ENTRIES_TYPEDOWN
            );
            addTransactionComplete(recipientWell, transactionStartTime, transactionId);
        }
    } catch {
        addTransactionFailed(recipientWell, transactionStartTime, transactionId);
    } finally {
        removePendingRequest(correlationId);
    }
});

export function getPersonaResults(
    serverResult: FindPeopleResponseMessage | SubstrateSearchSuggestionsResponse
): PersonaType[] {
    if (shouldUse3SPeopleSuggestions()) {
        let personas = [];
        processSubstrateSuggestionsResponse(
            personas,
            serverResult as SubstrateSearchSuggestionsResponse,
            false
        );
        return personas;
    } else {
        return (serverResult as FindPeopleResponseMessage).ResultSet;
    }
}

function isServerResultEmpty(
    serverResult: FindPeopleResponseMessage | SubstrateSearchSuggestionsResponse
): boolean {
    if (shouldUse3SPeopleSuggestions()) {
        const result = serverResult as SubstrateSearchSuggestionsResponse;
        return !result || !result.Groups;
    } else {
        const result = serverResult as FindPeopleResponseMessage;
        return result.ResponseClass != 'Success' || result.ResultSet.length == 0;
    }
}
